import { skipToken } from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  getAnnumsForFarm,
  getFarms,
  getInternalAnnumsForAllFarms,
} from "regen-api-ts-client";

import { useRegenQuery } from "../../util/apiHooks";
import { FARM_WIDE_FIELD } from "../../util/constants";
import { getDefaultAnnum, useHasSomeRoles } from "../../util/hooks";
import { buildMapPath } from "../../util/pathHelpers";
import { VDiv } from "../Div";
import { adminTabContents, generateModuleTabContents } from "../Tabs/data";

interface NavTabsProps {
  showAdminTabs: boolean;
  onNavLinkClick?: () => void;
}

const NavTabs = ({ showAdminTabs, onNavLinkClick }: NavTabsProps) => {
  const { data: isAdminOrStaff } = useHasSomeRoles(["admin", "staff"]);
  const { pathname } = useLocation();
  const {
    farmId: paramFarmId,
    fieldId: paramFieldId,
    annum: urlAnnum,
  } = useParams();

  const { data: farmList } = useRegenQuery(getFarms, {});

  const farmId = useMemo(() => {
    let currentId: string | undefined = paramFarmId;
    // If a grower user has no farmId, redirect to the first farm in their
    // farm list (we don't currently support multiple farms for grower users
    // in the regen-web codebase, but the regen-api does)
    if (!isAdminOrStaff && !paramFarmId && farmList && farmList.length > 0) {
      currentId = farmList[0].id;
    }
    return currentId;
  }, [farmList, isAdminOrStaff, paramFarmId]);

  // Only get all annums across farms if we have no farm id and the user is an admin trying to view
  // admin pages.
  const { data: allAnnums } = useRegenQuery(
    getInternalAnnumsForAllFarms,
    showAdminTabs ? {} : skipToken,
  );
  const { data: farmAnnums } = useRegenQuery(
    getAnnumsForFarm,
    farmId
      ? {
          path: { farm_id: farmId },
        }
      : skipToken,
  );

  // Set the default annum for the farm (based on the farm id from the url or from the grower's
  // farm list). If no farm id is present, only use the list of all annums across farms on admin
  // pages.
  const defaultAnnum = useMemo(() => {
    const annums = farmAnnums ? farmAnnums.annums : allAnnums?.annums;
    const latest = farmAnnums ? farmAnnums.latestAnnum : allAnnums?.latest;
    return annums && latest ? getDefaultAnnum(annums, latest) : undefined;
  }, [allAnnums, farmAnnums]);

  const annum = useMemo(
    () => (urlAnnum ? +urlAnnum : defaultAnnum),
    [defaultAnnum, urlAnnum],
  );

  const fieldId = paramFieldId || FARM_WIDE_FIELD.id;
  const showAdminStyle =
    isAdminOrStaff &&
    (pathname.includes("admin") || pathname.includes("user-profile"));

  const tabContents = useMemo(() => {
    // If the user is an admin or staff member and not previewing a farm,
    // just show the admin tabs
    if (showAdminTabs) {
      return adminTabContents;
    }

    if (farmId && annum && defaultAnnum) {
      // If the url annum is greater than the default (since we sometimes show users the
      // Field List page for the upcoming annum), reset to the default when navigating away
      // from the page.
      const tabAnnum =
        pathname.includes("field-map") && annum > defaultAnnum
          ? defaultAnnum
          : annum;

      // Embed the farmId into the various tab routes
      return generateModuleTabContents(farmId, fieldId, tabAnnum);
    }

    return [];
  }, [farmId, fieldId, annum, defaultAnnum, pathname, showAdminTabs]);

  const getTabClasses = useCallback(
    (label: string, route: string) => {
      const newPathname = pathname.replace(/\/+$/, "");
      // Check map path separately so that we don't need an exact match for datasources and layers. The
      // field properties tab only redirects to the map so it should never be active.
      const isActive =
        label === "Map" && farmId && annum
          ? newPathname.startsWith(buildMapPath(farmId, annum))
          : newPathname === route && label !== "Field Properties";
      return clsx({
        "items-center p-3 rounded-lg w-full cursor-pointer overflow-hidden hover:bg-secondary-light":
          true,
        "text-secondary-medium hover:text-primary": !isActive && showAdminStyle,
        "text-primary": isActive || !showAdminStyle,
        "bg-secondary-light border border-primary-dark": isActive,
      });
    },
    [pathname, showAdminStyle, annum, farmId],
  );

  return (
    <VDiv data-component="NavTabs" className="gap-2">
      {tabContents.map((tab) => (
        <Link
          to={tab.route}
          key={tab.label}
          className={getTabClasses(tab.label, tab.route)}
          data-component="Tab"
          data-testid={tab.label}
        >
          <div onClick={onNavLinkClick} className="flex items-center">
            {showAdminTabs ? (
              <i className={`fa-regular ${tab.icon} w-4`}></i>
            ) : (
              <img src={tab.icon} className="w-8" />
            )}
            <span className="w-full ml-4 text-md truncate">{tab.label}</span>
          </div>
        </Link>
      ))}
    </VDiv>
  );
};

export default NavTabs;
